import React, { useState, useEffect, useRef } from 'react';
import { Info } from 'lucide-react';
import { logger } from '../../lib/logger';

interface SVGIconProps {
  url: string;
  color: string;
  className?: string;
}

export const SVGIcon: React.FC<SVGIconProps> = ({ url, color, className = '' }) => {
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const translationsMap = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (!url) {
      setError(true);
      setIsLoading(false);
      return;
    }

    // Use localStorage as a simple cache for SVGs
    const cacheKey = `svg_${url}`;
    const cachedSvg = localStorage.getItem(cacheKey);
    
    if (cachedSvg) {
      try {
        // Apply color to the cached SVG
        const coloredSvg = applySvgColor(cachedSvg, color);
        setSvgContent(coloredSvg);
        setIsLoading(false);
        setError(false);
      } catch (e) {
        // If coloring fails, fetch again
        fetchSvg();
      }
      return;
    }

    fetchSvg();

    function fetchSvg() {
      const controller = new AbortController();
      const { signal } = controller;

      setIsLoading(true);
      setError(false);

      fetch(url, {
        signal,
        headers: {
          'Accept': 'image/svg+xml, */*',
          'Cache-Control': 'no-cache'
        }
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`Failed to load SVG: ${response.status}`);
          }
          return response.text();
        })
        .then(text => {
          // Basic validation that it's an SVG
          if (!text.includes('<svg') || !text.includes('</svg>')) {
            throw new Error('Invalid SVG content');
          }

          // Store original SVG in localStorage cache
          try {
            localStorage.setItem(cacheKey, text);
          } catch (e) {
            // Ignore storage errors
          }

          // Apply color to the SVG
          const coloredSvg = applySvgColor(text, color);
          setSvgContent(coloredSvg);
          setIsLoading(false);
        })
        .catch(error => {
          if (error.name !== 'AbortError') {
            logger.error('Error loading SVG', {
              category: 'UI',
              data: { url, error: error.message }
            });
            setError(true);
            setIsLoading(false);
          }
        });

      return () => {
        controller.abort();
      };
    }
  }, [url, color]);

  // Function to apply color to SVG content
  const applySvgColor = (svgText: string, color: string): string => {
    // Check if we already have a colored version of this SVG
    const cacheKey = `${svgText}_${color}`;
    if (translationsMap.current.has(cacheKey)) {
      return translationsMap.current.get(cacheKey)!;
    }

    try {
      // First, add a style attribute to the SVG element
      let result = svgText.replace(/<svg/, `<svg style="fill: ${color}; color: ${color};"`);
      
      // Explicitly set fill attributes where they exist
      result = result.replace(/fill="[^"]*"/g, `fill="${color}"`);
      result = result.replace(/fill='[^']*'/g, `fill='${color}'`);
      
      // Also handle stroke for outlined icons
      result = result.replace(/stroke="[^"]*"/g, `stroke="${color}"`);
      result = result.replace(/stroke='[^']*'/g, `stroke='${color}'`);
      
      // Cache the result
      translationsMap.current.set(cacheKey, result);
      
      return result;
    } catch (error) {
      logger.error('Error applying color to SVG', {
        category: 'UI',
        data: { error, color }
      });
      return svgText;
    }
  };

  // Show a simple colored div while loading
  if (isLoading) {
    return (
      <div className={`${className} flex items-center justify-center bg-transparent rounded-full animate-pulse`}>
        <div className="w-1/2 h-1/2 rounded-full" style={{ backgroundColor: 'transparent' }}></div>
      </div>
    );
  }

  // Show a fallback icon if there was an error
  if (error || !svgContent) {
    return (
      <div className={`${className} flex items-center justify-center`}>
        <Info size="50%" color={color} />
      </div>
    );
  }

  // Render the SVG
  return (
    <div 
      className={className}
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
};